import React, { useState, useEffect } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { articleService } from "../services/articleService";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Skeleton from "@mui/material/Skeleton";
import CircularProgress from "@mui/material/CircularProgress";
import { Article } from "../services/articleService";

const Research: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [loadedImages, setLoadedImages] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [errorImages, setErrorImages] = useState<{ [key: string]: boolean }>(
    {}
  );

  const handleImageLoad = (imageUrl: string) => {
    setLoadedImages((prev) => ({ ...prev, [imageUrl]: true }));
  };

  const handleImageError = (imageUrl: string) => {
    setErrorImages((prev) => ({ ...prev, [imageUrl]: true }));
  };

  // Get search param from URL
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get("search")?.toLowerCase().trim() || "";

  // Prepare articles with tags
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        const articlesData = await articleService.getArticles();

        const currentLang = (i18n.language || "en") as "en" | "ru";
        const hasLangContent = (langContent: any): boolean => {
          if (!langContent) return false;
          const hasTitle = Boolean(
            langContent.title && String(langContent.title).trim()
          );
          const hasContentTitle = Boolean(
            langContent.contentTitle && String(langContent.contentTitle).trim()
          );
          const content = langContent.content;
          const hasContent = Array.isArray(content)
            ? content.length > 0
            : Boolean(content && String(content).trim());
          return hasTitle || hasContentTitle || hasContent;
        };

        const processedArticles = articlesData
          .map((a: any) => {
            // Supabase article with bilingual content object
            if (
              a.content &&
              typeof a.content === "object" &&
              (a.content.en || a.content.ru)
            ) {
              const langContent = a.content[currentLang];
              // If no content for the selected language, drop the article from the list
              if (!hasLangContent(langContent)) return null;

              return {
                ...a,
                title: langContent.title || "",
                meta: langContent.meta || "",
                contentTitle: langContent.contentTitle || "",
                content: langContent.content || "",
                tags: langContent.tags as string[] | undefined,
              };
            }

            // Hardcoded article using translation keys (always show)
            return {
              ...a,
              title: t(a.titleKey),
              meta: t(a.metaKey),
              contentTitle: t(a.contentTitleKey),
              content: t(a.contentKey, { returnObjects: true }),
              tags: t(a.tagsKey, { returnObjects: true }) as
                | string[]
                | undefined,
            };
          })
          .filter(Boolean);

        setArticles(processedArticles as any[]);
      } catch (error) {
        console.error("Error loading articles:", error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, [t, i18n.language]);

  // Filter by tag if search query is present
  const filteredArticles = searchQuery
    ? articles.filter(
        (article: any) =>
          Array.isArray(article.tags) &&
          article.tags.some((tag: any) =>
            tag.toLowerCase().includes(searchQuery)
          )
      )
    : articles;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h1"
        component="h1"
        gutterBottom
        align="left"
        sx={{ textTransform: "uppercase" }}
      >
        {t("research.title")}
      </Typography>
      <Typography variant="body1" align="left" sx={{ mt: 4 }}>
        <Link
          href="http://najp.us/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "inherit",
            textDecoration: "none",
            "&:hover": {
              color: "#f4ad24",
            },
          }}
        >
          {t("research.description1")}
        </Link>
      </Typography>
      <Typography variant="body1" align="left" sx={{ mt: 4 }}>
        <Link
          href="https://tinyurl.com/y2ufq2th"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "inherit",
            textDecoration: "none",
            "&:hover": {
              color: "#f4ad24",
            },
          }}
        >
          {t("research.description2")}
        </Link>
      </Typography>
      <Typography
        variant="h1"
        component="h1"
        gutterBottom
        align="left"
        sx={{ mt: 6, textTransform: "uppercase" }}
      >
        {t("articles.title")}
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {searchQuery && filteredArticles.length === 0 && (
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Try to refine your search
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 4,
              mt: 2,
            }}
          >
            {filteredArticles.map((article: any, idx: number) => (
              <Box
                key={idx}
                sx={{
                  flex: { xs: "1 1 100%", sm: "1 1 48%" },
                  minWidth: { xs: "100%", sm: "320px" },
                  maxWidth: { xs: "100%", sm: "48%" },
                  boxSizing: "border-box",
                  mb: 4,
                }}
              >
                <Card
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: 2,
                  }}
                >
                  <RouterLink
                    to={`/research-and-articles/article/${article.slug}`}
                    style={{ display: "block" }}
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: "smooth" })
                    }
                  >
                    <Box sx={{ position: "relative", height: "40vh" }}>
                      {!loadedImages[article.image] &&
                        !errorImages[article.image] && (
                          <Skeleton
                            variant="rectangular"
                            width="100%"
                            height="100%"
                            animation="wave"
                          />
                        )}
                      {errorImages[article.image] ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "grey.200",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {t("common.imageLoadError")}
                          </Typography>
                        </Box>
                      ) : (
                        <CardMedia
                          component="img"
                          image={article.image}
                          alt={article.title}
                          loading="lazy"
                          onLoad={() => handleImageLoad(article.image)}
                          onError={() => handleImageError(article.image)}
                          sx={{
                            cursor: "pointer",
                            height: "100%",
                            width: "100%",
                            objectFit: "cover",
                            opacity: loadedImages[article.image] ? 1 : 0,
                            transition: "opacity 0.3s ease-in-out",
                          }}
                        />
                      )}
                    </Box>
                  </RouterLink>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      {article.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      {article.meta}
                    </Typography>
                    <Divider
                      sx={{
                        borderBottom: "2px solid #f3c96b",
                        width: 60,
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontSize: "1.2rem", fontWeight: 700, mb: 1 }}
                    >
                      {article.contentTitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 9,
                        WebkitBoxOrient: "vertical",
                        textOverflow: "ellipsis",
                        fontSize: "1.2rem",
                        lineHeight: 1.6,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: Array.isArray(article.content)
                          ? (() => {
                              const paragraphs = article.content.filter(
                                (block: any) => block.type === "paragraph"
                              );
                              const previewText = paragraphs
                                .slice(0, 9)
                                .map((block: any) => block.text)
                                .join("<br/><br/>");
                              return (
                                previewText +
                                (paragraphs.length > 9 ? "..." : "")
                              );
                            })()
                          : String(article.content),
                      }}
                    />
                  </CardContent>
                  <CardActions
                    sx={{ justifyContent: "space-between", px: 2, pb: 2 }}
                  >
                    <Link
                      component={RouterLink}
                      to={`/research-and-articles/article/${article.slug}`}
                      underline="hover"
                      sx={{ fontSize: 14 }}
                      onClick={() =>
                        window.scrollTo({ top: 0, behavior: "smooth" })
                      }
                    >
                      {t("articles.readMore")}
                    </Link>
                    {/* <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "text.secondary",
                    fontSize: 16,
                  }}
                >
                  <Box component="span" sx={{ ml: 0.5 }}>
                    0
                  </Box>
                  <Box component="span" sx={{ ml: 0.5 }}>
                    💬
                  </Box>
                </Box> */}
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Container>
  );
};

export default Research;
